import { toast } from 'sonner'
export { formatDate } from '@shared/utils/date'
// Constants
const DEFAULT_CURRENCY = 'USD'
const DEFAULT_DECIMALS = 2
const MAX_DECIMALS = 10

// Helper function to validate numbers
const isValidNumber = (value: unknown): value is number => {
  return typeof value === 'number' && !isNaN(value) && isFinite(value)
}

/**
 * Formats currency amounts into parts for flexible rendering.
 * @param amount Raw amount, potentially in smallest currency unit.
 * @param currency The ISO currency code.
 * @param decimal The number of decimal places for the smallest unit.
 * @returns An object with the formatted amount, currency code, and full string.
 */
export const formatCurrencyParts = (
  amount: number,
  currency: string = DEFAULT_CURRENCY,
  decimal?: number
): { amount: string; currency: string; full: string } => {
  // Validate and set defaults
  if (!isValidNumber(amount)) {
    return { amount: "0.00", currency, full: `0.00 ${currency}` }
  }

  const validCurrency = (typeof currency === "string" && currency.trim()) ? currency : DEFAULT_CURRENCY
  const validDecimal = decimal !== undefined && isValidNumber(decimal) && decimal >= 0 && decimal <= MAX_DECIMALS 
    ? decimal 
    : DEFAULT_DECIMALS

  const actualAmount = decimal !== undefined ? amount / Math.pow(10, validDecimal) : amount
  const displayDecimals = validDecimal
  
  // Use Intl.NumberFormat for numeric formatting only (no currency symbol)
  const formatter = new Intl.NumberFormat('en-US', {
    minimumFractionDigits: displayDecimals,
    maximumFractionDigits: displayDecimals
  })
  
  const formattedNumber = formatter.format(actualAmount)
  const currencyCode = validCurrency.toUpperCase()

  return {
    amount: formattedNumber,
    currency: currencyCode,
    full: `${formattedNumber} ${currencyCode}`,
  }
}

/**
 * Formats currency amounts with proper decimal handling
 * @param amount - Raw amount from backend
 * @param currency - Currency code (e.g., 'USD', 'BGN')
 * @param decimal - Decimal places from backend (required)
 * @returns Formatted currency string
 */
export const formatCurrency = (
  amount: number, 
  currency: string = DEFAULT_CURRENCY, 
  decimal?: number
): string => {
  return formatCurrencyParts(amount, currency, decimal).full
}

/**
 * Formats a number as a percentage
 * @param value The numeric value to format
 * @param decimals Number of decimal places (default: 1)
 * @returns Formatted percentage string
 */
export const formatPercentage = (value: number, decimals = 1): string => {
  if (!isValidNumber(value)) {
    return '0.0%'
  }
  
  const safeDecimals = isValidNumber(decimals) && decimals >= 0 && decimals <= MAX_DECIMALS ? decimals : 1
  return `${value.toFixed(safeDecimals)}%`
}

/**
 * Calculates conversion rate
 * @param conversions Number of conversions
 * @param views Total number of views
 * @returns Conversion rate as percentage
 */
export const calculateConversionRate = (conversions: number, views: number): number => {
  if (!isValidNumber(conversions) || !isValidNumber(views) || conversions < 0 || views <= 0) {
    return 0
  }
  
  return (conversions / views) * 100
}

/**
 * Formats a percentage change with appropriate sign
 * @param change Percentage change value
 * @returns Formatted percentage string
 */
export const formatPercentageChange = (change: number): string => {
  if (!isValidNumber(change)) {
    return '0.0%'
  }
  
  const sign = change > 0 ? '+' : ''
  return `${sign}${change.toFixed(1)}%`
}

/**
 * Copies an entity ID to clipboard with consistent messaging
 * @param id The ID to copy
 * @param entityType The type of entity (e.g., 'Payment', 'Customer', 'Merchant')
 */
export const copyEntityId = async (id: string, entityType: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(id)
    toast.success(`${entityType} ID copied to clipboard`)
  } catch {
    toast.error(`Failed to copy ${entityType} ID`)
  }
}

/**
 * Creates standardized batch action handlers for entities
 * @param entityType The type of entity (e.g., 'payment', 'customer', 'merchant')
 * @returns Object with common batch action functions
 */
export const createBatchActions = (entityType: string) => {
  return {
    handleBatchFlag: (selectedIds: string[]) => {
      toast.success(`${selectedIds.length} ${entityType}s flagged`)
    },
    handleBatchExport: (selectedIds: string[]) => {
      toast.success(`Exporting ${selectedIds.length} ${entityType}s`)
    },
    handleBatchDelete: (selectedIds: string[]) => {
      toast.success(`${selectedIds.length} ${entityType}s deleted`)
    },
    handleBatchUpdate: (selectedIds: string[], action: string) => {
      toast.success(`${selectedIds.length} ${entityType}s ${action}`)
    }
  }
}